import { Check, Loader2, X } from "lucide-react"
import { cn } from "@vc/lib/utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { ACCESS_REQUEST_COPY, type PresenterRequestSubject } from "@vc/views/dialogs/ScreenShareRequestDialog"

/**
 * Where my own request to present stands, at the top right of the video area.
 *
 * The waiting used to be a dialog in the middle of the screen that stayed until
 * the host answered, so the person who asked could not use the meeting, or even
 * see it properly, in the meantime. Now the ask sends and the dialog closes;
 * this chip says it is waiting, with a cross to take it back, and turns into
 * the button that starts the share once the host approves (the browser only
 * opens a screen picker on a click, so the approval cannot start it alone).
 */
export function MyRequestChip({
  subject,
  approved,
  onCancel,
  onStart,
  onDismiss,
}: {
  subject: PresenterRequestSubject
  /** False while waiting for the host. */
  approved: boolean
  /** Take the request back while it waits. */
  onCancel: () => void
  /** Approved: start what was asked for. */
  onStart: () => void
  /** Approved: put it away without starting. */
  onDismiss: () => void
}) {
  const t = useTranslation()
  const copy = ACCESS_REQUEST_COPY[subject]
  const Icon = copy.icon

  return (
    <div className="absolute right-6 top-6 z-[26] max-w-[calc(100%-3rem)]">
      <div
        className={cn(
          "flex items-center gap-3 rounded-2xl py-2 pl-2 pr-2 text-white shadow-lg shadow-black/20 backdrop-blur-xl",
          "border border-white/15 ring-1 ring-white/10",
          approved ? "bg-emerald-600/90" : "bg-slate-900/60",
        )}
      >
        <div className="relative grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/15">
          <Icon className="h-4 w-4" />
          {!approved && <Loader2 className="absolute -right-1 -top-1 h-4 w-4 animate-spin text-amber-300" />}
          {approved && (
            <span className="absolute -right-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-white text-emerald-600">
              <Check className="h-3 w-3" />
            </span>
          )}
        </div>
        <p className="min-w-0 flex-1 text-xs font-medium leading-snug line-clamp-2" title={t(approved ? copy.granted : copy.waiting)}>
          {t(approved ? copy.granted : copy.waiting)}
        </p>
        {approved && (
          <button
            type="button"
            onClick={onStart}
            className="shrink-0 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-white/90"
          >
            {t(copy.start)}
          </button>
        )}
        <button
          type="button"
          onClick={approved ? onDismiss : onCancel}
          aria-label={t(approved ? "common.close" : "common.cancel")}
          title={t(approved ? "common.close" : "common.cancel")}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-white/80 transition hover:bg-white/15 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
